import { createElement } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Trash2, Eye, FileText, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { type Anexo, isImagemAnexo, formatarTamanho, getLabelTipoAnexo, getIconTipoAnexo } from './types';

interface Props {
  anexo: Anexo;
  numeroPedido?: string;
  onDelete: (anexo: Anexo) => void;
  onPreview?: (anexo: Anexo) => void;
  deletando: boolean;
}

export function AnexoCard({ anexo, numeroPedido, onDelete, onPreview, deletando }: Props) {
  const isImagem = isImagemAnexo(anexo.mime);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {isImagem ? (
              <ImageIcon className="w-8 h-8 text-info" />
            ) : (
              <FileText className="w-8 h-8 text-muted-foreground" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{anexo.nome}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-xs">
                {createElement(getIconTipoAnexo(anexo.tipo_anexo), { className: 'mr-1 inline h-3 w-3 align-[-2px] text-muted-foreground' })}{getLabelTipoAnexo(anexo.tipo_anexo)}
              </Badge>
              {numeroPedido && (
                <Badge variant="outline" className="text-xs font-mono">
                  #{numeroPedido}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs">
                {anexo.tipo_extensao.toUpperCase()}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatarTamanho(anexo.tamanho)}
              </span>
              <span className="text-xs text-muted-foreground">
                {format(new Date(anexo.created_at), "dd/MM/yy 'às' HH:mm", { locale: ptBR })}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {isImagem && onPreview && (
              <Button size="sm" variant="ghost" onClick={() => onPreview(anexo)} title="Visualizar">
                <Eye className="w-4 h-4" />
              </Button>
            )}
            <Button size="sm" variant="ghost" asChild title="Baixar">
              <a href={anexo.url} download={anexo.nome} target="_blank" rel="noopener noreferrer">
                <Download className="w-4 h-4" />
              </a>
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => onDelete(anexo)}
              disabled={deletando}
              title="Excluir"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
